// ─────────────────────────────────────────────────────────────────────────────
// Mack · CALENDAR tab (build-01). Extracted from MackPage.tsx in build-04.
// SOURCE: scratchpad/mack_design_refs/build-01_tab-02_calendar.png
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useRef } from 'react';
import './CalendarTab.css';
import { C, MONO } from './tokens';
import { calendarStatusMessage, calendarFailure, calendarSetAsideReasons, calendarWeek, refreshMackCalendar, checkMackCalendarStatus, setMackCalendarAlert, stopMackCalendar, useMackCalendar } from '../../lib/mackCalendar';
import type { MackCalendarStatus, CalendarWindow } from '../../lib/mackCalendar';
import { card, panelTitle, panelNote, outlinePill, microLabel, Chip } from './primitives';
import { siteName } from '../../lib/mackCalculatorView';   // the forecast's source by its site name, never the printed address

// ── TYPES — these mirror Forge's stores, field-for-field ─────────────────────

// macro_data/mack_calendar.json → scheduled[]
export interface MackDownstreamDestination {
  tab: string;
  sheet: string;
  block: string;
  workbook_named_input: string;
  consumer: string;
}
export interface MackPulseDetails {
  pulse_state?: 'Scheduled' | 'Running' | 'Updated' | 'Awaiting publication' | 'Source unavailable' | 'Release mismatch' | 'Parse failed' | null;
  pulse_reason?: string | null;
  pulse_attempted_at?: string | null;
  pulse_source?: string | null;
  pulse_next_reconciliation_at?: string | null;
}
export interface MackScheduledEvent extends MackPulseDetails {
  event_id: string;
  day_label: string;      // store-held display grouping, e.g. 'TODAY · SAT 25 JUL'
  time_local: string;     // store-held display time, Hobart-local, e.g. '22:30'
  name: string;
  subtitle: string;
  country: string;        // filter key — US | UK | EUR | JPY | AUD
  impact: 'high' | 'medium' | 'low' | 'UNKNOWN';
  consensus: string;
  wakes_sheet: string;    // sheet code — USD | GBP | EUR | JPY | AUD | BLOC
  wakes_block: string;    // Policy | Prices | Growth | External | Positioning | Risk
  alert_set: boolean;
  // CONTRACT ADDITION (Pixel → Forge, build-01 — ACCEPTED by owner): the
  // countdown cannot be computed from day_label + time_local, which are display
  // strings, not an instant. This is the absolute anchor the countdown reads.
  // Forge: emit ISO-8601 with offset (his pulse store already does at
  // next_catalyst_at). A row without it renders '—' rather than guessing.
  starts_at?: string;
  scheduled_at?: string;
  currency?: string;
  unit?: string | null;
  period?: string;
  previous?: string | null;
  forecast?: string | null;
  actual?: string | null;
  event_date?: string;
  timezone?: string;
  source_timezone?: string | null;
  source_refs?: { page_id?: string; field?: string; url: string; quote?: string }[];
  grade?: string | null;
  freshness?: { state?: string; producer?: string } | null;
  release_status?: 'SCHEDULED' | 'RELEASED' | 'UNKNOWN' | string;
  source_url?: string;
  source_name?: string;
  request_id?: string;
  job_id?: string;
  trace_id?: string;
  pulse_at?: string | null;
  pulse_registered?: boolean;
  series_id?: string;
  history_id?: string;
  vintage?: string | null;
  revision_index?: number;
  original_actual?: string | null;
  publication_at?: string | null;
  retrieved_at_utc?: string | null;
  downstream_destinations?: MackDownstreamDestination[];
}


// macro_data/mack_calendar.json → unscheduled[]
export interface MackUnscheduledEvent {
  headline: string;
  channel: string;                // transmission channel, not headline size
  mapped: boolean;                // false → NOT MAPPED
  state: 'ARMED' | 'WATCH' | 'NOISE' | 'HELD';
  recheck_condition: string;      // rendered in place of the mapped flag when set
}

// macro_data/mack_pulse.json → sheets[]
export interface MackSheetPulse {
  sheet: string;                              // store code: USD | GBP | EUR | JPY | AUD | BLOC
  activation: Record<string, number>;         // the six blocks
  activation_display: number;
  activation_display_block: string;
  goes_quiet: string;
  next_wake: string;
}

// Sheet code → the label the DESIGN uses. Confirmed in the image: the wake tags
// read "US · Prices" / "UK · Prices" (not USD/GBP), and the forecast table reads
// US / UK / EUR / JPY / AUD / Reserve bloc. Display labels in BOTH places — this
// corrects the code-vs-label split from the previous pass.
const SHEET_LABEL: Record<string, string> = {
  USD: 'US', GBP: 'UK', EUR: 'EUR', JPY: 'JPY', AUD: 'AUD', BLOC: 'Reserve bloc',
};

// Compatibility for Overview's existing import; Calendar never supplies sample events.
export const MACK_CALENDAR_PLACEHOLDER: { scheduled: MackScheduledEvent[]; unscheduled: MackUnscheduledEvent[]; pulse: MackSheetPulse[] } = {
  scheduled: [], unscheduled: [], pulse: [],
};

// ── HELPERS ──────────────────────────────────────────────────────────────────

// Sampled from the design: high = red, medium = amber. No 'low' row appears in
// the reference, so its colour is the muted tertiary — the only impact value
// without a design pixel behind it.
const IMPACT_COLOUR: Record<MackScheduledEvent['impact'], string> = {
  high: C.red, medium: C.amberState, low: C.blue, UNKNOWN: C.textMuted,
};

const STATE_COLOUR: Record<MackUnscheduledEvent['state'], string> = {
  ARMED: C.red, WATCH: C.amberState, NOISE: C.textMuted, HELD: C.blue,
};

// Countdown format, read off the design: '2h 14m' · '2h 59m' · '5h' · '1d' ·
// '2d' · '6d' · '9d' · '12d'. No 'in ' prefix, no d+h pairing, minutes only on
// the near horizon. The exact hour at which minutes drop is bracketed by the
// design between 2h59m (shows them) and 5h (does not) — 3h is the read.
export function countdown(startsAt: string | undefined, now: number): string {
  if (!startsAt) return '—';
  const t = Date.parse(startsAt);
  if (Number.isNaN(t)) return '—';
  const ms = t - now;
  const suffix = ms < 0 ? ' ago' : '';
  const mins = Math.floor(Math.abs(ms) / 60000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor(mins / 60);
  if (d >= 1) return `${d}d${suffix}`;
  if (h >= 3) return `${h}h${suffix}`;
  if (h >= 1) return `${h}h ${mins % 60}m${suffix}`;
  return `${mins}m${suffix}`;
}

// Imminent countdowns are amber, day-scale ones recede to muted — sampled at
// every row in the design (#e8963c under 24h, #a99a8c at 1d and beyond).
function countdownColour(startsAt: string | undefined, now: number): string {
  if (!startsAt) return C.textMuted;
  const t = Date.parse(startsAt);
  if (Number.isNaN(t)) return C.textMuted;
  const ms = t - now;
  return ms < 86400000 ? C.amber : C.textSub;
}

// Day grouping preserves store order — the store emits chronologically, and
// re-sorting here would silently disagree with it.
function groupByDay(events: MackScheduledEvent[]): { day: string; rows: MackScheduledEvent[] }[] {
  const out: { day: string; rows: MackScheduledEvent[] }[] = [];
  events.forEach(e => {
    const last = out[out.length - 1];
    if (last && last.day === e.day_label) last.rows.push(e);
    else out.push({ day: e.day_label, rows: [e] });
  });
  return out;
}

// ── SMALL PARTS ──────────────────────────────────────────────────────────────

function BellIcon({ filled }: { filled: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"
         fill={filled ? 'currentColor' : 'none'}
         stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2.2a3.9 3.9 0 0 0-3.9 3.9c0 2.45-.58 3.7-1.15 4.4-.3.36-.05.9.4.9h9.3c.45 0 .7-.54.4-.9-.57-.7-1.15-1.95-1.15-4.4A3.9 3.9 0 0 0 8 2.2z" />
      <path d="M6.5 13.4a1.6 1.6 0 0 0 3 0" fill="none" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"
         fill="none" stroke="currentColor" strokeWidth="1.35"
         strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.1 6.1A5.2 5.2 0 0 0 3.5 4.2L2.4 5.7" />
      <path d="M2.2 2.6v3.2h3.2" />
      <path d="M2.9 9.9a5.2 5.2 0 0 0 9.6 1.9l1.1-1.5" />
      <path d="M13.8 13.4v-3.2h-3.2" />
    </svg>
  );
}


// ── CALENDAR TAB ─────────────────────────────────────────────────────────────

const COUNTRY_FILTERS = [
  ['All', 'All'], ['USD', 'US / USD'], ['GBP', 'UK / GBP'], ['EUR', 'Eurozone / EUR'],
  ['JPY', 'Japan / JPY'], ['AUD', 'Australia / AUD'], ['NZD', 'New Zealand / NZD'],
  ['CAD', 'Canada / CAD'], ['CHF', 'Switzerland / CHF'],
] as const;
const IMPACT_FILTERS  = ['All', 'High', 'Medium', 'Low'] as const;

const displayValue = (value: string | null | undefined) =>
  value == null || value.trim() === '' || /^(UNKNOWN|ABSENT|N\/?A|NULL)$/i.test(value.trim()) ? '—' : value;

export function CalendarTab({ authToken, onAlertCount }: {
  authToken?: string | null; onAlertCount?: (count: number) => void;
  armed?: Set<string>; onToggle?: (id: string) => void;
}) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [historical, setHistorical] = useState(false);
  const [historyDates, setHistoryDates] = useState(() => calendarWeek());
  const [historyWindow, setHistoryWindow] = useState(historyDates);
  const selectedWindow: CalendarWindow = useMemo(() => historical ? { ...historyWindow, view: 'historical' }
    : calendarWeek(weekOffset), [historical, historyWindow, weekOffset]);
  const calendar = useMackCalendar(authToken, selectedWindow);
  const scheduled = calendar.view?.scheduled;
  const unscheduled = calendar.view?.unscheduled ?? [];
  const forecast = calendar.view?.activation_forecast ?? [];
  const alertCount = calendar.view?.alerts.length ?? 0;
  useEffect(() => { onAlertCount?.(alertCount); }, [onAlertCount, alertCount]);
  const [alertBusy, setAlertBusy] = useState<string | null>(null);

  const [country, setCountry] = useState<string>('All');
  const [series, setSeries] = useState('All');
  const [impact, setImpact]   = useState<typeof IMPACT_FILTERS[number]>('All');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<MackCalendarStatus | null>(null);
  const refreshInFlight = useRef(false);
  const [stopping, setStopping] = useState(false);
  const mounted = useRef(true);
  const refreshVersion = useRef(0);
  useEffect(() => { refreshVersion.current += 1; }, [authToken]);

  // Countdowns are live. One minute is the smallest unit rendered, so a
  // 30s tick keeps them honest without churning the tree.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    mounted.current = true;
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => { mounted.current = false; clearInterval(id); };
  }, []);

  const filtered = useMemo(() => (scheduled ?? []).filter(e => {
    if (country !== 'All' && e.currency !== country) return false;
    if (series !== 'All' && (e.series_id || e.name) !== series) return false;
    if (impact !== 'All' && e.impact !== impact.toLowerCase()) return false;
    return true;
  }), [scheduled, country, impact, series]);
  const availableSeries = useMemo(() => Array.from(new Map((scheduled ?? []).map(event =>
    [event.series_id || event.name, event.name])).entries()).sort((a, b) => a[1].localeCompare(b[1])), [scheduled]);
  const validHistoryDates = /^\d{4}-\d{2}-\d{2}$/.test(historyDates.window_start_date)
    && /^\d{4}-\d{2}-\d{2}$/.test(historyDates.window_end_date)
    && historyDates.window_start_date <= historyDates.window_end_date;

  const retainedStart = calendar.view?.history_start_date;
  const retainedEnd = calendar.view?.history_end_date;
  const hasRetainedRange = !!retainedStart && !!retainedEnd && /^\d{4}-\d{2}-\d{2}$/.test(retainedStart)
    && /^\d{4}-\d{2}-\d{2}$/.test(retainedEnd) && retainedStart <= retainedEnd;
  const showRetainedHistory = () => {
    if (hasRetainedRange) {
      const range = { window_start_date: retainedStart!, window_end_date: retainedEnd! };
      setHistoryDates(range);
      setHistoryWindow(range);
    }
    setHistorical(true);
  };
  const days = useMemo(() => groupByDay(filtered), [filtered]);
  const onRefresh = async () => {
    if (refreshInFlight.current || historical) return;
    refreshInFlight.current = true;
    const version = ++refreshVersion.current;
    setRefreshing(true);
    setStopping(false);
    setRefreshStatus({ kind: 'running', code: 'CALENDAR_ACQUIRING', message: 'Calendar acquisition running...' });
    try {
      const status = await refreshMackCalendar(selectedWindow);
      if (mounted.current && refreshVersion.current === version && !status.ignored) {
        calendar.apply(status.view);
        setRefreshStatus(status);
        void calendar.reload();
      }
    } catch (failure) {
      const status = calendarFailure(failure);
      if (mounted.current && refreshVersion.current === version && !status.ignored) setRefreshStatus(status);
    } finally {
      refreshInFlight.current = false;
      if (mounted.current) setRefreshing(false);
    }
  };

  const onCheckStatus = async () => {
    const version = refreshVersion.current;
    const status = await checkMackCalendarStatus();
    if (mounted.current && refreshVersion.current === version && !status.ignored) {
      setRefreshStatus(status);
      if (status.kind === 'updated') void calendar.reload();
    }
  };

  const onStop = async () => {
    if (stopping) return;
    setStopping(true);
    try {
      const status = await stopMackCalendar();
      if (mounted.current) setRefreshStatus(status);
    } catch (failure) {
      if (mounted.current) { setRefreshStatus(calendarFailure(failure)); setStopping(false); }
    }
  };

  const onToggle = async (event: MackScheduledEvent) => {
    if (alertBusy || historical) return;
    setAlertBusy(event.event_id);
    try {
      const view = await setMackCalendarAlert(event.event_id, !event.alert_set, selectedWindow);
      if (mounted.current) calendar.apply(view);
    } catch (failure) {
      if (mounted.current) setRefreshStatus(calendarFailure(failure));
    } finally { if (mounted.current) setAlertBusy(null); }
  };
  const localStamp = (value?: string | null) => {
    if (!value || Number.isNaN(Date.parse(value))) return '—';
    return new Date(value).toLocaleString('en-AU', { timeZone: 'Australia/Hobart' });
  };

  return (
    <div className="mack-calendar-root" style={{ display: 'flex', flexDirection: 'column', gap: 17, minWidth: 0 }}>
      {refreshStatus && <button type="button" onClick={() => void onCheckStatus()}>Check Calendar status</button>}
      {calendar.error && (
        <p role="status" data-calendar-status={calendar.error.kind}
           style={{ ...panelNote, overflowWrap: 'anywhere' }}>{calendarStatusMessage(calendar.error)}{calendar.view ? ` · Stale: showing last successfully read Calendar data${calendar.view.window_start_date ? ` (${calendar.view.window_start_date} – ${calendar.view.window_end_date})` : ''}.` : ''}</p>
      )}
      {refreshStatus && (
        <p role="status" data-calendar-status={refreshStatus.kind}
           style={{ ...panelNote, overflowWrap: 'anywhere' }}>{calendarStatusMessage(refreshStatus)}</p>
      )}

      {/* ── HEADER PANEL — title, description, pills AND the filters ─────
          One card, per the design: the filter rows sit inside the same panel
          as the heading, not in a separate strip. */}
      <div style={{ ...card, padding: '18px 22px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 100%', minWidth: 0 }}>
            <div style={panelTitle}>Calendar</div>
            <div style={{ ...panelNote, maxWidth: 700 }}>
              Scheduled releases required by the downstream model, with retained revisions. Set an alert and it
              pushes on the morning of, plus a T−2h warning. Every row shows which sheet
              and block it wakes — the calendar is also the activation forecast.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={onRefresh}
              disabled={refreshing || historical}
              aria-busy={refreshing}
              aria-label="Refresh Calendar"
              title={historical ? 'Historical view reads retained observations' : 'Refresh Calendar'}
              style={{
                minWidth: 175, height: 30, borderRadius: 8, cursor: refreshing || historical ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                border: `1px solid ${C.chipOffBd}`, background: 'transparent',
                color: refreshing ? C.amber : C.textMuted, padding: '0 10px',
              }}
            >
              <RefreshIcon />
              <span>{refreshing ? 'Refreshing Calendar...' : 'Refresh Calendar'}</span>
            </button>
            {refreshing && <button aria-label="Calendar Off" disabled={stopping} onClick={() => void onStop()}>
              {stopping ? 'Stopping Calendar…' : 'Off'}
            </button>}
            <span style={outlinePill(C.blue)}>{calendar.view?.total_rows ?? 0} stored events</span>
            <span style={outlinePill(C.blue)}>{filtered.length} filtered events</span>
            <span style={outlinePill(C.red)}>
              {alertCount} {alertCount === 1 ? 'Alert' : 'Alerts'}
            </span>
            <span style={outlinePill(C.blue)}>Source: {calendar.view?.source || 'FXStreet'}</span>
          </div>
        </div>

        <div style={{ ...panelNote, marginTop: 12 }}>
          {selectedWindow.window_start_date} – {selectedWindow.window_end_date} · Australia/Hobart
          <br />Last successful update: {localStamp(calendar.view?.updated_at || undefined)}
        </div>
        <nav aria-label="Calendar week" className="mack-calendar-navigation">
          <button disabled={refreshing} onClick={() => { setHistorical(false); setWeekOffset(-1); }}>Previous Week</button>
          <button disabled={refreshing} onClick={() => { setHistorical(false); setWeekOffset(0); }}>Current Week</button>
          <button disabled={refreshing} onClick={() => { setHistorical(false); setWeekOffset(1); }}>Next Week</button>
          <button disabled={refreshing} aria-pressed={historical} onClick={showRetainedHistory}>Historical</button>
        </nav>
        {historical && <div className="mack-calendar-history-controls">
          <label>From<input type="date" aria-label="Historical start date" value={historyDates.window_start_date}
            onChange={event => setHistoryDates(value => ({ ...value, window_start_date: event.target.value }))} /></label>
          <label>To<input type="date" aria-label="Historical end date" value={historyDates.window_end_date}
            onChange={event => setHistoryDates(value => ({ ...value, window_end_date: event.target.value }))} /></label>
          <button disabled={!validHistoryDates || calendar.state === 'loading'}
            onClick={() => setHistoryWindow({ ...historyDates })}>Apply date range</button>
          {hasRetainedRange && <button onClick={showRetainedHistory} disabled={calendar.state === 'loading'}>All retained history</button>}
          {!validHistoryDates && <span role="status">Choose an end date on or after the start date.</span>}
          <p style={panelNote}>{calendar.view?.history_count ?? 0} retained vintages · {calendar.view?.history_series_count ?? 0} series
            {calendar.view?.history_start_date && ` · Stored coverage: ${calendar.view.history_start_date} – ${calendar.view.history_end_date}`}</p>
        </div>}
        {historical && calendar.view?.history_retention === 'PERMANENT' && <p style={panelNote}>
          Calendar history is retained permanently. Date filters select stored releases.
        </p>}
        {historical && calendar.view?.history_workbook && <p style={{ ...panelNote, overflowWrap: 'anywhere' }}>
          History workbook: {calendar.view.history_workbook.name}
          {calendar.view.history_workbook.event_count !== null && calendar.view.history_workbook.vintage_count !== null
            ? ` · ${calendar.view.history_workbook.event_count} events · ${calendar.view.history_workbook.vintage_count} vintages` : ' · Counts unavailable'}
          {calendar.view.history_workbook.state === 'AVAILABLE' ? ' · Available' : ' · Workbook publication pending'}
          {calendar.view.history_workbook.updated_at && ` · Updated: ${localStamp(calendar.view.history_workbook.updated_at)}`}
        </p>}
        {calendar.view?.calendar_completion && <div style={{ ...panelNote, overflowWrap: 'anywhere' }} data-calendar-completion>
          <span>Calendar coverage: {calendar.view.calendar_completion.state} · {calendar.view.calendar_completion.reason}</span>
          {calendar.view.calendar_completion.attempted_at && <span> · Attempted: {localStamp(calendar.view.calendar_completion.attempted_at)}</span>}
          {calendar.view.calendar_completion.source_url && <> · <a href={calendar.view.calendar_completion.source_url} target="_blank" rel="noreferrer" style={{ color: C.blue }}>Coverage source</a></>}
          {calendar.view.calendar_completion.next_reconciliation_at && <span> · Next permitted reconciliation: {localStamp(calendar.view.calendar_completion.next_reconciliation_at)}</span>}
        </div>}
        {!!calendar.view?.coverage_gap_count && <p style={panelNote}>{calendar.view.coverage_gap_count} downstream coverage gaps remain in the installed contracts.</p>}
        {historical && calendar.view?.official_observation_count !== undefined && <p style={panelNote}>
          {calendar.view.official_observation_count} validated official Actual observations retained separately.
        </p>}
        {historical && !!calendar.view?.official_observation_exclusions.length && <details style={panelNote}>
          <summary>{calendar.view.official_observation_exclusions.length} official observations excluded from validated history</summary>
          <ul>{Array.from(new Set(calendar.view.official_observation_exclusions)).map(reason => <li key={reason}>{reason}</li>)}</ul>
        </details>}
        {calendar.state === 'loading' && <div style={panelNote}>Reading stored Calendar…</div>}
        {!!calendar.view?.set_aside.length && <details style={panelNote}>
          <summary>{calendar.view.set_aside.length} records set aside</summary>
          <ul>{calendarSetAsideReasons(calendar.view.set_aside).map(reason => <li key={reason}>{reason}</li>)}</ul>
        </details>}

        {/* Filters — COUNTRY and IMPACT side by side, label above each chip row */}
        <div style={{ display: 'flex', gap: 24, marginTop: 22, flexWrap: 'wrap', minWidth: 0 }}>
          <div>
            <div style={{ ...microLabel, marginBottom: 9 }}>Country / currency</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {COUNTRY_FILTERS.map(([key, label]) => (
                <Chip key={key} label={label} active={country === key} onClick={() => setCountry(key)} />
              ))}
            </div>
          </div>
          <label className="mack-calendar-series-filter">
            <span style={{ ...microLabel, marginBottom: 9 }}>Series / event</span>
            <select aria-label="Calendar series" value={series} onChange={event => setSeries(event.target.value)}>
              <option value="All">All series</option>
              {availableSeries.map(([id, label]) => <option key={id} value={id}>Series: {label}</option>)}
            </select>
          </label>
          <div>
            <div style={{ ...microLabel, marginBottom: 9 }}>Impact</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {IMPACT_FILTERS.map(i => (
                <Chip key={i} label={i} active={impact === i} onClick={() => setImpact(i)} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mack-calendar-body">

        {/* LEFT — day-grouped event list. Card padding is vertical only so the
            armed-row tint can run the full width of the panel, as in the design. */}
        <div className="mack-calendar-list" style={{ ...card, padding: '6px 0 2px', minWidth: 0 }}>
          {days.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: C.textMuted, fontSize: 13 }}>
              {calendar.state === 'loading' ? 'Reading stored Calendar…' : !scheduled?.length ? 'No Calendar history has been acquired for this period.' : 'No events match this filter.'}
            </div>
          )}

          {days.map(({ day, rows }) => (
            <div key={day}>
              <div style={{
                ...microLabel,
                color: C.amber, fontSize: 10, letterSpacing: '0.16em',
                padding: '18px 18px 10px',
                borderBottom: `1px solid ${C.rule}`,
              }}>
                {day}
              </div>

              {rows.map(e => {
                const isArmed = e.alert_set;
                return (
                  <div key={e.history_id || e.event_id} data-calendar-event-id={e.event_id} data-calendar-history-id={e.history_id}
                    className="mack-calendar-event-row"
                    style={{ background: isArmed ? C.armedRow : 'transparent', borderBottom: `1px solid ${C.rule}` }}>
                    <div className="mack-calendar-time" style={{ color: C.textSub, fontFamily: MONO }}>
                      <strong>{e.time_local || '—'}</strong>
                      <small>{e.day_label}</small>
                      <small title={e.timezone}>Hobart</small>
                    </div>
                    <span className="mack-calendar-impact" title={`${e.impact} impact`}
                      style={{ background: IMPACT_COLOUR[e.impact] }} />
                    <div className="mack-calendar-name" style={{ color: C.text }}>
                      <strong>{e.name}</strong>
                      <div style={{ color: C.textMuted }}>
                        {e.impact === 'UNKNOWN' ? '—' : e.impact.toUpperCase()} · {e.country}{e.currency && e.currency !== e.country ? ` / ${e.currency}` : ''}
                        {e.period ? ` · ${e.period}` : ''}
                      </div>
                    </div>
                    <div className="mack-calendar-values" style={{ color: C.textSub, fontFamily: MONO }}>
                      <div><small>Actual</small><span title={displayValue(e.actual)}>{displayValue(e.actual)}</span></div>
                      <div><small>Forecast</small><span title={displayValue(e.forecast ?? e.consensus)}>{displayValue(e.forecast ?? e.consensus)}</span></div>
                      <div><small>Previous</small><span title={displayValue(e.previous)}>{displayValue(e.previous)}</span></div>
                    </div>
                    <div className="mack-calendar-details" style={{ color: C.textMuted }}>
                      <span>Unit: {displayValue(e.unit)}</span>
                      <span>Status: {displayValue(e.release_status)}</span>
                      {e.source_url && <a href={e.source_url} target="_blank" rel="noreferrer" title={e.source_url} style={{ color: C.blue }}>{e.source_name || (e.source_url.includes('fxstreet.com') ? 'FXStreet' : 'Source')}</a>}
                      {e.subtitle && <span title={e.subtitle}>{e.subtitle}</span>}
                      {e.wakes_sheet !== '—' && <span title={`${e.wakes_sheet} · ${e.wakes_block}`}>
                        {SHEET_LABEL[e.wakes_sheet] ?? e.wakes_sheet} · {e.wakes_block}
                      </span>}
                      {!!e.downstream_destinations?.length && <span className="mack-calendar-lineage">
                        Destinations: {e.downstream_destinations.map(destination => `${destination.tab} · ${destination.sheet} · ${destination.block}`).join('; ')}
                      </span>}
                      {(e.pulse_at || e.pulse_state) && <span>Mack Pulse: {localStamp(e.pulse_at)} · {e.pulse_state || '—'}</span>}
                      {e.pulse_attempted_at && <span>Attempted: {localStamp(e.pulse_attempted_at)}</span>}
                      {e.pulse_source && <a href={e.pulse_source} target="_blank" rel="noreferrer" style={{ color: C.blue }}>Pulse source</a>}
                      {e.pulse_reason && <span className="mack-calendar-pulse-reason">Reason: {e.pulse_reason}</span>}
                      {e.pulse_next_reconciliation_at && <span>Next permitted reconciliation: {localStamp(e.pulse_next_reconciliation_at)}</span>}
                      {historical && <div className="mack-calendar-vintage">
                        <span>Vintage: {displayValue(e.vintage)} · Revision: {e.revision_index ?? '—'}</span>
                        <span>Original Actual: {displayValue(e.original_actual)}</span>
                        <span>Publication: {localStamp(e.publication_at)}</span>
                        <span>Retrieved: {localStamp(e.retrieved_at_utc)}</span>
                        {e.downstream_destinations?.map((destination, index) => <span key={index}>
                          {destination.workbook_named_input || 'Workbook input unbound'}{destination.consumer ? ` · ${destination.consumer}` : ''}
                        </span>)}
                      </div>}
                      <span style={{ color: countdownColour(e.starts_at, now) }}>{countdown(e.starts_at || e.scheduled_at, now)}</span>
                    </div>
                    <button className="mack-calendar-bell" onClick={() => void onToggle(e)} disabled={alertBusy !== null || historical}
                      aria-pressed={isArmed} aria-label={isArmed ? `Alert on for ${e.name}` : `Alert off for ${e.name}`}
                      title={isArmed ? 'Alert set — morning of, plus T−2h' : 'Set alert'}
                      style={{ border: `1px solid ${isArmed ? C.chipBorder : C.chipOffBd}`,
                        background: isArmed ? C.chipBg : 'transparent', color: isArmed ? C.amberBright : C.textMuted }}>
                      <BellIcon filled={isArmed} />
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* RIGHT COLUMN */}
        <div className="mack-calendar-aside" style={{ display: 'flex', flexDirection: 'column', gap: 17 }}>

          {/* ── ACTIVATION FORECAST ────────────────────────────────── */}
          <div style={{ ...card, padding: '18px 20px 16px' }}>
            <div style={panelTitle}>Activation forecast</div>
            <div style={panelNote}>Admitted release activations and Pulse outcomes · Hobart time.</div>
            {!forecast.length && <p style={panelNote}>No upcoming activations in this stored period.</p>}
            {forecast.map(item => <div key={item.event_id} className="mack-calendar-forecast" style={{ padding: '12px 0', borderBottom: `1px solid ${C.rule}`, color: C.textSub }}>
              <strong>{item.event_name}</strong>
              <div title={`${item.affected_sheet || '—'} · ${item.affected_block || '—'}`}>
                {item.affected_sheet || '—'} · {item.affected_block || '—'} · {item.currency} · {item.impact}
              </div>
              <dl>
                <dt>Release time</dt><dd>{localStamp(item.release_at)}</dd>
                <dt>Mack Pulse time</dt><dd>{localStamp(item.pulse_at)}</dd>
                <dt>Pulse state</dt><dd>{item.pulse_state || '—'}</dd>
                {item.pulse_attempted_at && <><dt>Attempted</dt><dd>{localStamp(item.pulse_attempted_at)}</dd></>}
                {item.pulse_source && <><dt>Source</dt><dd><a href={item.pulse_source} title={item.pulse_source} target="_blank" rel="noopener noreferrer" style={{ color: C.blue }}>{siteName(item.pulse_source) || 'Pulse source'}<span aria-hidden="true"> ↗</span></a></dd></>}
                {item.pulse_reason && <><dt>Reason</dt><dd>{item.pulse_reason}</dd></>}
                {item.pulse_next_reconciliation_at && <><dt>Next reconciliation</dt><dd>{localStamp(item.pulse_next_reconciliation_at)}</dd></>}
                {!!item.downstream_destinations?.length && <><dt>Destinations</dt><dd>{item.downstream_destinations.map(destination =>
                  `${destination.tab} · ${destination.sheet} · ${destination.block}`).join('; ')}</dd></>}
              </dl>
            </div>)}
          </div>

          {/* ── UNSCHEDULED ────────────────────────────────────────── */}
          <div style={{ ...card, padding: '18px 20px 8px' }}>
            <div style={panelTitle}>Unscheduled — webhook push</div>
            <div style={panelNote}>
              Classified by transmission channel, not by how large the headline sounds.
              Unmapped events are held, not discarded.
            </div>

            <div style={{ marginTop: 14 }}>
              {unscheduled.map((u, i) => (
                <div key={u.headline} style={{
                  display: 'flex', alignItems: 'center', gap: 13,
                  padding: '15px 0',
                  borderTop: i === 0 ? 'none' : `1px solid ${C.rule}`,
                }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                    background: STATE_COLOUR[u.state],
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.35 }}>{u.headline}</div>
                    <div style={{ fontSize: 12, color: C.textMuted, fontFamily: MONO, marginTop: 5 }}>
                      {/* The design composes this line: channel · MAPPED / NOT MAPPED,
                          except HELD, which shows its re-check condition instead. */}
                      {u.channel} · {u.recheck_condition || (u.mapped ? 'MAPPED' : 'NOT MAPPED')}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 12, fontFamily: MONO, flexShrink: 0,
                    textTransform: 'uppercase', letterSpacing: '0.12em',
                    color: STATE_COLOUR[u.state],
                  }}>
                    {u.state}
                  </span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
